import styled from 'styled-components';

import { COLORS, MOBILE, STATUS_COLORS } from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';
import { Button } from '../../../../components/Button';

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Label = styled(Text)`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/** The empty state: the drop target, with the Generate offer under it. */
export const EmptyStack = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

/** Empty-state drop target (click to pick a file). */
export const DropZone = styled.button`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 140px;
  padding: 20px;
  border: 1px dashed ${COLORS.panelBorder};
  border-radius: 12px;
  background: ${COLORS.surfaceSoft};
  color: ${COLORS.textMuted};
  cursor: pointer;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: ${COLORS.surfaceFaint};
  }

  &:disabled {
    cursor: default;
  }
`;

export const DropHint = styled(Text)`
  font-size: 14px;
  color: ${COLORS.textMuted};
`;

/**
 * `stretch`, not `flex-start`: the column beside the picture has to be as tall
 * as the picture for its action row to land on the picture's bottom edge.
 */
export const PreviewRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;

  ${MOBILE} {
    flex-direction: column;
  }
`;

/** 16:9 thumbnail of the uploaded hero. Wraps the image so it can host the generating wash. */
export const ThumbFrame = styled.div`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 240px;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  flex: 0 0 auto;
  /* The row stretches its children; the picture keeps its own aspect ratio. */
  align-self: flex-start;

  ${MOBILE} {
    width: 100%;
  }
`;

export const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/**
 * The column beside the thumbnail. It matches the thumbnail's height, and the
 * summary lines are pushed up so the action row sits on the picture's BOTTOM
 * EDGE: the two halves of the hero then read as one block instead of as a
 * picture with some buttons floating next to it.
 */
export const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
  width: 100%;
  align-self: stretch;
  /* Summary at the top, actions at the bottom, the slack between them. */
  justify-content: space-between;

  ${MOBILE} {
    /* Stacked, there is no picture edge to line up with. */
    align-self: auto;
  }
`;

/** The two summary lines, kept together at the top of the column. */
export const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

/** One line of the alt/caption summary: what the dialog would open on. */
export const MetaLine = styled.div`
  display: flex;
  gap: 8px;
  min-width: 0;
`;

export const MetaLabel = styled(Text)`
  flex: none;
  width: 62px;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

export const MetaValue = styled(Text)<{ $missing: boolean }>`
  font-size: 13px;
  line-height: 1.45;
  color: ${(p) => (p.$missing ? COLORS.textFaint : COLORS.text)};
  font-style: ${(p) => (p.$missing ? 'italic' : 'normal')};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
`;

/**
 * What an author does to the hero, as real secondary buttons.
 *
 * They were borderless text links, which put three of the page's actions in the
 * one place where they read as captions of the image rather than as things to
 * press. The outline variant is the app's secondary CTA, so they now match the
 * add buttons in the SEO lists and the outline buttons in the header.
 *
 * Shorter than the app-wide 48px control: they sit against a 135px thumbnail,
 * beneath two lines of summary, and full-height pills there would outweigh the
 * image they act on.
 */
export const ActionButton = styled(Button)`
  height: 36px;
  padding: 0 14px;
  font-size: 14px;
  gap: 6px;
`;

/** Remove is the one that destroys something, and is tinted to say so. */
export const DestructiveButton = styled(ActionButton)`
  border-color: ${STATUS_COLORS.negative};
  color: ${STATUS_COLORS.negative};

  span {
    color: ${STATUS_COLORS.negative};
  }
`;

/** Upload progress line, shown under the picker while a file is in flight. */
export const Status = styled(Text)`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

export const ErrorText = styled(Text)`
  font-size: 13px;
  color: ${STATUS_COLORS.negative};
`;
